import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Users, Eye, Inbox, MessagesSquare, Lock, LogOut, Loader2, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import Logo from "../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ate_admin_token";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-2xl p-5 bg-white border border-fuchsia-100 shadow-sm">
    <div className="flex items-center justify-between">
      <span className="text-xs font-mono-accent uppercase tracking-wider text-slate-500">{label}</span>
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: tint }}>
        <Icon size={17} className="text-fuchsia-600" />
      </span>
    </div>
    <div className="mt-3 font-display font-extrabold text-3xl text-slate-900">{value}</div>
  </div>
);

const BarList = ({ title, items }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="rounded-2xl p-6 bg-white border border-fuchsia-100 shadow-sm">
      <h3 className="font-display font-bold text-slate-900 mb-4">{title}</h3>
      {items.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-700 capitalize truncate">{it.name}</span>
              <span className="text-slate-500 font-medium">{it.count}</span>
            </div>
            <div className="h-2 rounded-full bg-fuchsia-50 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(it.count / max) * 100}%`, background: "linear-gradient(90deg,#E933C6,#8B5CF6)" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = ({ data, onLogout }) => (
  <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
    <div className="flex items-center justify-between mb-8">
      <div>
        <Logo variant="light" />
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 mt-3">Analytics</h1>
        <p className="text-slate-500 text-sm">Private dashboard · live traffic & leads</p>
      </div>
      <button onClick={onLogout} data-testid="analytics-logout" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50">
        <LogOut size={16} /> Sign out
      </button>
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={Users} label="Unique Visitors" value={data.unique_visitors} tint="rgba(233,51,198,0.1)" />
      <StatCard icon={Eye} label="Page Views" value={data.total_pageviews} tint="rgba(139,92,246,0.1)" />
      <StatCard icon={Inbox} label="Leads" value={data.leads_count} tint="rgba(233,51,198,0.1)" />
      <StatCard icon={MessagesSquare} label="Chat Conversations" value={data.chat_sessions} tint="rgba(139,92,246,0.1)" />
    </div>

    <div className="mt-6 rounded-2xl p-6 bg-white border border-fuchsia-100 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={18} className="text-fuchsia-600" />
        <h3 className="font-display font-bold text-slate-900">Last 14 days</h3>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E933C6" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#E933C6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1e6f4" vertical={false} />
            <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <Tooltip />
            <Area type="monotone" dataKey="views" stroke="#E933C6" fill="url(#gv)" strokeWidth={2} name="Views" />
            <Area type="monotone" dataKey="visitors" stroke="#8B5CF6" fill="url(#gu)" strokeWidth={2} name="Visitors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="mt-6 grid md:grid-cols-3 gap-4">
      <BarList title="Most-visited sections" items={data.top_sections} />
      <BarList title="Traffic sources" items={data.top_referrers} />
      <BarList title="Devices" items={data.devices} />
    </div>

    <div className="mt-6 rounded-2xl p-6 bg-white border border-fuchsia-100 shadow-sm">
      <h3 className="font-display font-bold text-slate-900 mb-4">Recent leads</h3>
      {data.recent_leads.length === 0 && <p className="text-sm text-slate-400">No leads yet.</p>}
      <div className="divide-y divide-slate-100">
        {data.recent_leads.map((l, i) => (
          <div key={i} className="py-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-slate-800">{l.name}</div>
              <div className="text-sm text-slate-500">{l.email}{l.company ? ` · ${l.company}` : ""}</div>
            </div>
            <div className="text-xs text-slate-400">{(l.created_at || "").slice(0, 10)}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LoginGate = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      setError(err.response?.status === 401 ? "Incorrect password." : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "#0b0912" }}>
      <div className="absolute inset-0 hero-glow opacity-60" />
      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} data-testid="analytics-login-form"
        className="relative z-10 w-full max-w-sm glass-dark rounded-3xl p-8"
      >
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg,#E933C6,#8B5CF6)" }}>
          <Lock size={22} className="text-white" />
        </div>
        <h1 className="font-display font-bold text-white text-xl">Analytics Portal</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">Enter your password to view traffic.</p>
        <input
          data-testid="analytics-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoFocus
          className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-fuchsia-400/60"
        />
        {error && <p className="text-fuchsia-300 text-sm mt-3" data-testid="analytics-login-error">{error}</p>}
        <button
          type="submit" disabled={busy}
          data-testid="analytics-login-button"
          className="btn-glow mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />} Unlock
        </button>
      </motion.form>
    </div>
  );
};

export default function Analytics() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (t) => {
    setLoading(true);
    axios
      .get(`${API}/analytics/summary`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  };

  if (!token) return <LoginGate onSuccess={(t) => setToken(t)} />;
  if (loading || !data)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf9fc]">
        <Loader2 className="animate-spin text-fuchsia-500" size={28} />
      </div>
    );
  return (
    <div className="min-h-screen bg-[#faf9fc]">
      <Dashboard data={data} onLogout={logout} />
    </div>
  );
}
